import { createSlice } from "@reduxjs/toolkit";
import {
  getYears,
  getBrands,
  getModels,
  getVariants,
  postExchangeAPI,
} from "../../api/exchange";

export const exchangeSlice = createSlice({
  name: "preExchange",
  initialState: {
    years: [],
    brands: [],
    models: [],
    variants: [],
    loadingYears: false,
    loadingBrands: false,
    loadingModels: false,
    loadingVariants: false,
    yearsError: {
      message: "",
      haveError: false,
    },
    brandsError: {
      message: "",
      haveError: false,
    },
    modelsError: {
      message: "",
      haveError: false,
    },
    variantsError: {
      message: "",
      haveError: false,
    },
  },
  reducers: {
    setYearsLoading: (state) => {
      state.loadingYears = true;
      state.yearsError = { message: "", haveError: false };
    },
    setBrandsLoading: (state) => {
      state.loadingBrands = true;
      state.brandsError = { message: "", haveError: false };
    },
    setModelsLoading: (state) => {
      state.loadingModels = true;
      state.modelsError = { message: "", haveError: false };
    },
    setVariantsLoading: (state) => {
      state.loadingVariants = true;
      state.variantsError = { message: "", haveError: false };
    },

    yearsSuccess: (state, action) => {
      state.years = action.payload;
      state.loadingYears = false;
      state.yearsError = { message: "", haveError: false };
    },
    brandsSuccess: (state, action) => {
      state.brands = action.payload;
      state.loadingBrands = false;
      state.brandsError = { message: "", haveError: false };
    },
    modelsSuccess: (state, action) => {
      state.models = action.payload;
      state.loadingModels = false;
      state.modelsError = { message: "", haveError: false };
    },
    variantsSuccess: (state, action) => {
      state.variants = action.payload;
      state.loadingVariants = false;
      state.variantsError = { message: "", haveError: false };
    },

    yearsFailed: (state, action) => {
      state.loadingYears = false;
      state.yearsError = {
        message: action.payload,
        haveError: true,
      };
    },
    brandsFailed: (state, action) => {
      state.loadingBrands = false;
      state.brandsError = {
        message: action.payload,
        haveError: true,
      };
    },
    modelsFailed: (state, action) => {
      state.loadingModels = false;
      state.modelsError = {
        message: action.payload,
        haveError: true,
      };
    },
    variantsFailed: (state, action) => {
      state.loadingVariants = false;
      state.variantsError = {
        message: action.payload,
        haveError: true,
      };
    },

    clearYears: (state) => {
      state.years = [];
      state.yearsError = { message: "", haveError: false };
    },
    clearBrands: (state) => {
      state.brands = [];
      state.brandsError = { message: "", haveError: false };
    },
    clearModels: (state) => {
      state.models = [];
      state.modelsError = { message: "", haveError: false };
    },
    clearVariants: (state) => {
      state.variants = [];
      state.variantsError = { message: "", haveError: false };
    },

    clearAll: (state) => {
      state.years = [];
      state.brands = [];
      state.models = [];
      state.variants = [];
      state.loadingYears = false;
      state.loadingBrands = false;
      state.loadingModels = false;
      state.loadingVariants = false;
      state.yearsError = { message: "", haveError: false };
      state.brandsError = { message: "", haveError: false };
      state.modelsError = { message: "", haveError: false };
      state.variantsError = { message: "", haveError: false };
    },

    resetYearsError: (state) => {
      state.yearsError = { message: "", haveError: false };
    },
    resetBrandsError: (state) => {
      state.brandsError = { message: "", haveError: false };
    },
    resetModelsError: (state) => {
      state.modelsError = { message: "", haveError: false };
    },
    resetVariantsError: (state) => {
      state.variantsError = { message: "", haveError: false };
    },
  },
});

export const {
  setYearsLoading,
  setBrandsLoading,
  setModelsLoading,
  setVariantsLoading,
  yearsSuccess,
  brandsSuccess,
  modelsSuccess,
  variantsSuccess,
  yearsFailed,
  brandsFailed,
  modelsFailed,
  variantsFailed,
  clearYears,
  clearBrands,
  clearModels,
  clearVariants,
  clearAll,
  resetYearsError,
  resetBrandsError,
  resetModelsError,
  resetVariantsError,
} = exchangeSlice.actions;

export const selectYears = (state) => state.preExchange.years || [];
export const selectBrands = (state) => state.preExchange.brands || [];
export const selectModels = (state) => state.preExchange.models || [];
export const selectVariants = (state) => state.preExchange.variants || [];
export const selectYearsLoading = (state) => state.preExchange.loadingYears;
export const selectBrandsLoading = (state) => state.preExchange.loadingBrands;
export const selectModelsLoading = (state) => state.preExchange.loadingModels;
export const selectVariantsLoading = (state) =>
  state.preExchange.loadingVariants;
export const selectYearsError = (state) => state.preExchange.yearsError;
export const selectBrandsError = (state) => state.preExchange.brandsError;
export const selectModelsError = (state) => state.preExchange.modelsError;
export const selectVariantsError = (state) => state.preExchange.variantsError;

export const selectHasYears = (state) => state.preExchange.years.length > 0;
export const selectHasBrands = (state) => state.preExchange.brands.length > 0;
export const selectHasModels = (state) => state.preExchange.models.length > 0;
export const selectHasVariants = (state) =>
  state.preExchange.variants.length > 0;

export const fetchYears = () => async (dispatch) => {
  try {
    dispatch(setYearsLoading());
    const response = await getYears();
    const years = response?.data || response;

    if (Array.isArray(years)) {
      dispatch(yearsSuccess(years));
    } else {
      dispatch(yearsFailed("Invalid response format for years"));
    }
  } catch (err) {
    const errorMessage =
      err?.response?.data?.message || err?.message || "Failed to fetch years";
    dispatch(yearsFailed(errorMessage));
  }
};

export const fetchBrands = (inputData) => async (dispatch) => {
  try {
    dispatch(setBrandsLoading());
    const response = await getBrands(inputData);
    const brands = response?.data || response;

    if (Array.isArray(brands)) {
      dispatch(brandsSuccess(brands));
    } else {
      dispatch(brandsFailed("Invalid response format for brands"));
    }
  } catch (err) {
    const errorMessage =
      err?.response?.data?.message || err?.message || "Failed to fetch brands";
    dispatch(brandsFailed(errorMessage));
  }
};

export const fetchModels = (inputData) => async (dispatch) => {
  try {
    dispatch(setModelsLoading());
    const response = await getModels(inputData);
    const models = response?.data || response;

    if (Array.isArray(models)) {
      dispatch(modelsSuccess(models));
    } else {
      dispatch(modelsFailed("Invalid response format for models"));
    }
  } catch (err) {
    const errorMessage =
      err?.response?.data?.message || err?.message || "Failed to fetch models";
    dispatch(modelsFailed(errorMessage));
  }
};

export const fetchVariants = (inputData) => async (dispatch) => {
  try {
    dispatch(setVariantsLoading());
    const response = await getVariants(inputData);
    const variants = response?.data || response;

    if (Array.isArray(variants)) {
      dispatch(variantsSuccess(variants));
    } else {
      dispatch(variantsFailed("Invalid response format for variants"));
    }
  } catch (err) {
    const errorMessage =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to fetch variants";
    dispatch(variantsFailed(errorMessage));
  }
};

export const fetchBrandsAndClearRest = (inputData) => async (dispatch) => {
  dispatch(clearBrands());
  dispatch(clearModels());
  dispatch(clearVariants());
  dispatch(fetchBrands(inputData));
};

export const fetchModelsAndClearVariants = (inputData) => async (dispatch) => {
  dispatch(clearVariants());
  dispatch(fetchModels(inputData));
};

export const fetchVariantsForModel = (inputData) => async (dispatch) => {
  dispatch(clearVariants());
  dispatch(fetchVariants(inputData));
};

export const postExchange = (inputData) => async (dispatch) => {
  try {
    const response = await postExchangeAPI(inputData);
    return response;
  } catch (err) {
    return err;
  }
};

export default exchangeSlice.reducer;
